import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Clapperboard, Send, Loader2, Wand2, Users, ChevronDown, Sparkles, CheckCircle2, History,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

function AgentCard({ a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-white/10 bg-black/25 overflow-hidden" data-testid={`director-agent-${a.agent_id}`}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left hover:bg-white/[0.03]">
        <span className="text-lg leading-none">{a.emoji}</span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-bold truncate">{a.name}</span>
          <span className="block text-[11px] text-white/40 truncate">{a.role}</span>
        </span>
        {a.change && <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-emerald-500/15 border border-emerald-500/40 px-2 py-0.5 text-[10px] font-bold text-emerald-300"><Wand2 className="w-3 h-3" /> change</span>}
        <ChevronDown className={`w-4 h-4 text-white/40 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-3 pb-3 pt-1 space-y-1.5 text-xs">
          {a.findings && <p className="text-white/70"><span className="text-white/40">Findings: </span>{a.findings}</p>}
          {a.recommendation && <p className="text-white/70"><span className="text-white/40">Recommends: </span>{a.recommendation}</p>}
          {a.change && <p className="text-emerald-300/90"><span className="text-white/40">Proposed change: </span>{a.change.instruction}</p>}
        </div>
      )}
    </div>
  );
}

export default function GameDirector() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const [thread, setThread] = useState([]);   // {role, ...}
  const [req, setReq] = useState("");
  const [busy, setBusy] = useState(false);
  const [roster, setRoster] = useState([]);
  const [presets, setPresets] = useState([]);
  const [showTeam, setShowTeam] = useState(false);
  const [activity, setActivity] = useState(null);
  const [showActivity, setShowActivity] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { api.get("/workforce/agents").then((r) => { setRoster(r.data.agents || []); setPresets(r.data.presets || []); }).catch(() => {}); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [thread, busy]);

  const loadActivity = useCallback(async () => {
    try { const { data } = await api.get(`/workforce/${gameId}/activity`); setActivity(data.entries || []); }
    catch { setActivity([]); }
  }, [gameId]);

  const send = useCallback(async (text) => {
    const request = (text ?? req).trim();
    if (!request || busy) return;
    setThread((t) => [...t, { role: "user", text: request }]);
    setReq("");
    setBusy(true);
    try {
      const { data } = await api.post(`/workforce/${gameId}/direct`, { request }, { timeout: 150000 });
      setThread((t) => [...t, { role: "director", ...data }]);
      if (showActivity) loadActivity();
    } catch (e) {
      setThread((t) => [...t, { role: "director", error: true, director: { rationale: formatApiError(e.response?.data?.detail) || "The team hit a snag — try rephrasing." }, agents: [] }]);
    } finally { setBusy(false); }
  }, [req, busy, gameId, showActivity, loadActivity]);

  const applyingRef = useRef(false);
  const applyChanges = (instruction) => {
    if (applyingRef.current) return;
    applyingRef.current = true;
    try { sessionStorage.setItem("gv_pending_edit", instruction); } catch {}
    toast.success("Opening the builder with the team's changes ready…");
    setTimeout(() => navigate(`/app/build/${gameId}`), 700);
  };

  return (
    <div className="min-h-screen bg-[#070912] text-white flex flex-col" data-testid="game-director">
      <div className="max-w-3xl w-full mx-auto flex-1 flex flex-col p-5 lg:p-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/app/build/${gameId}`)} data-testid="director-back" className="text-white/70 hover:text-white"><ArrowLeft className="w-5 h-5" /></Button>
          <div className="flex items-center gap-2">
            <Clapperboard className="w-6 h-6 text-[#f59e0b]" />
            <h1 className="font-display text-xl lg:text-2xl font-extrabold tracking-tight">Game Director</h1>
          </div>
          <button onClick={() => setShowTeam((s) => !s)} data-testid="director-team-toggle"
            className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-white/5 border border-white/10 px-3 py-1 text-xs font-semibold text-white/70 hover:bg-white/10">
            <Users className="w-3.5 h-3.5" /> {roster.length} specialists
          </button>
          <button onClick={() => { const n = !showActivity; setShowActivity(n); if (n && activity === null) loadActivity(); }} data-testid="director-activity-toggle"
            className="inline-flex items-center gap-1.5 rounded-full bg-white/5 border border-white/10 px-3 py-1 text-xs font-semibold text-white/70 hover:bg-white/10">
            <History className="w-3.5 h-3.5" /> Activity
          </button>
        </div>
        <p className="text-sm text-white/45 mb-3">
          Tell the Director what you want. It delegates to only the specialists your request needs and shows you exactly who worked on it.
        </p>

        {/* One-tap Director missions */}
        {presets.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4" data-testid="director-presets">
            {presets.map((p) => (
              <button key={p.id} data-testid={`director-preset-${p.id}`} onClick={() => send(p.request)} disabled={busy}
                className="inline-flex items-center gap-1.5 rounded-full border border-[#f59e0b]/30 bg-[#f59e0b]/10 px-3 py-1.5 text-xs font-semibold text-[#fbbf24] hover:bg-[#f59e0b]/20 disabled:opacity-50 transition-colors">
                <span>{p.emoji}</span> {p.label}
              </button>
            ))}
          </div>
        )}

        {showActivity && (
          <div className="mb-4 rounded-2xl border border-white/10 bg-white/[0.03] p-4" data-testid="director-activity">
            <p className="text-[11px] uppercase tracking-wide text-white/40 mb-2 flex items-center gap-1"><History className="w-3 h-3" /> Team activity log</p>
            {activity === null ? (
              <p className="text-sm text-white/40">Loading…</p>
            ) : activity.length === 0 ? (
              <p className="text-sm text-white/40" data-testid="director-activity-empty">No team activity yet — direct your first request.</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {activity.map((e, i) => (
                  <div key={e.id} data-testid={`director-activity-${i}`} className="rounded-xl bg-black/25 border border-white/8 px-3 py-2">
                    <p className="text-sm text-white/85 truncate">{e.request}</p>
                    <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                      {(e.agents || []).map((a) => (
                        <span key={a.agent_id} className={`text-[10px] rounded-full px-1.5 py-0.5 border ${a.changed ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" : "border-white/12 bg-white/5 text-white/50"}`} title={a.name}>{a.emoji} {a.name}</span>
                      ))}
                      {e.is_large && <span className="text-[10px] rounded-full px-1.5 py-0.5 border border-[#f59e0b]/40 bg-[#f59e0b]/10 text-[#fbbf24]">Large</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {showTeam && (
          <div className="mb-4 grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="director-team-grid">
            {roster.map((a) => (
              <div key={a.id} className="rounded-xl border border-white/10 bg-white/[0.03] p-2.5" title={a.role}>
                <div className="text-lg">{a.emoji}</div>
                <div className="text-[11px] font-bold leading-tight mt-1">{a.name}</div>
                <div className="text-[10px] text-white/40 leading-tight">{a.role}</div>
              </div>
            ))}
          </div>
        )}

        {/* Thread */}
        <div className="flex-1 space-y-4 overflow-y-auto pb-4" data-testid="director-thread">
          {thread.length === 0 && (
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 text-sm text-white/60" data-testid="director-empty">
              <p className="font-semibold text-white/80 mb-2">Try directing:</p>
              {["Make the game harder and more exciting", "Add a boss fight with music and a health bar", "Polish the whole game — visuals, sound and feel", "Balance the combat so it's fair"].map((s) => (
                <button key={s} onClick={() => send(s)} className="block text-left text-[#fbbf24] hover:text-white transition-colors mb-1.5" data-testid="director-suggestion">
                  <Sparkles className="w-3.5 h-3.5 inline mr-1 -mt-0.5" /> {s}
                </button>
              ))}
            </div>
          )}
          {thread.map((m, i) => (
            <div key={i} data-testid={`director-msg-${i}`} className={m.role === "user" ? "flex justify-end" : ""}>
              {m.role === "user" ? (
                <div className="max-w-[85%] rounded-2xl rounded-br-md bg-[#f59e0b] text-black font-semibold px-4 py-2.5 text-sm">{m.text}</div>
              ) : (
                <div className="max-w-full space-y-3">
                  <div className={`rounded-2xl rounded-bl-md border px-4 py-3 text-sm ${m.error ? "border-rose-500/40 bg-rose-500/10 text-rose-200" : "border-[#f59e0b]/30 bg-[#f59e0b]/[0.08] text-white/90"}`}>
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="text-base">🎯</span><span className="font-bold text-sm">Game Director</span>
                      {m.director?.is_large && <span className="rounded-full bg-[#f59e0b]/20 border border-[#f59e0b]/50 px-2 py-0.5 text-[10px] font-bold text-[#fbbf24]" data-testid={`director-large-${i}`}>Large request</span>}
                    </div>
                    <p className="leading-relaxed">{m.director?.rationale}</p>
                  </div>
                  {m.agents?.length > 0 && (
                    <div data-testid={`director-agents-${i}`}>
                      <p className="text-[11px] uppercase tracking-wide text-white/40 mb-1.5 flex items-center gap-1"><Users className="w-3 h-3" /> {m.agents.length} agent{m.agents.length === 1 ? "" : "s"} worked on this</p>
                      <div className="space-y-1.5">
                        {m.agents.map((a) => <AgentCard key={a.agent_id} a={a} />)}
                      </div>
                    </div>
                  )}
                  {m.merged_change && (
                    <Button onClick={() => applyChanges(m.merged_change.instruction)} data-testid={`director-apply-${i}`}
                      className="rounded-full bg-gradient-to-r from-emerald-500 to-[#f59e0b] hover:opacity-90 text-black font-semibold">
                      <CheckCircle2 className="w-4 h-4" /> Apply team changes ({m.merged_change.summary})
                    </Button>
                  )}
                </div>
              )}
            </div>
          ))}
          {busy && (
            <div className="flex items-center gap-2 text-white/50 text-sm" data-testid="director-thinking">
              <Loader2 className="w-4 h-4 animate-spin text-[#f59e0b]" /> The Director is briefing the team…
            </div>
          )}
          <div ref={endRef} />
        </div>

        {/* Composer */}
        <div className="sticky bottom-0 bg-[#070912] pt-2">
          <div className="flex items-end gap-2 rounded-2xl border border-white/15 bg-white/[0.04] p-2">
            <textarea data-testid="director-input" value={req} onChange={(e) => setReq(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              rows={1} placeholder="Direct your team… e.g. 'add a turbo boost and a speed HUD'"
              className="flex-1 resize-none bg-transparent outline-none text-sm text-white placeholder:text-white/35 px-2 py-2 max-h-32" />
            <Button onClick={() => send()} disabled={busy || !req.trim()} data-testid="director-send"
              className="rounded-xl bg-[#f59e0b] hover:bg-[#d97706] text-black shrink-0">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
          <p className="text-[10px] text-white/30 text-center mt-1.5">The Director delegates to the specialists your request needs · changes flow through the Safe AI Change Engine</p>
        </div>
      </div>
    </div>
  );
}
